import { Component, OnInit, Signal, computed, inject } from '@angular/core';

import { InstructionsComponent } from '@r1-compass-library/components';
import { MatButtonModule } from '@angular/material/button';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { FontAwesomeModule, FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { faEllipsisVertical } from '@fortawesome/pro-solid-svg-icons';
import { AssetStateMessageHandlingService } from '@r1-compass-library/services/asset-state-message-handling.service';
import { filterNil } from 'ngxtension/filter-nil';
import { DataMartTablesStore } from '../../template-criteria/store/data-mart-tables.store';
import { MatMenuModule } from '@angular/material/menu';
import { ActivatedRoute, Router } from '@angular/router';
import { ContentAsset, ContentAssetRule, DataMartField } from '@r1-compass-library/models';
import { SelectedContentAssetStore } from '../store/selected-content-asset.store';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Uuid } from '@r1-compass-library/utilities/uuid';
import { GlobalLoggedInUserStore } from '@r1-compass-library/store/logged-in-user/global-logged-in-user.store';
import {
    FormUnsavedChangesWarningContentLibraryRules,
    noopFormSaveMutationResult,
} from '@r1-compass-library/guards/hasUnsavedChanges.guard';
import { BehaviorSubject, Subject } from 'rxjs';
import { SaveGuardStore } from '@r1-compass-library/guards/save-guard.store';
import { ContentLibraryRuleSummaryPipe } from './content-library-rule-summary.pipe';
import { ContentLibraryValidationStateService } from '../content-library-validation-state.service';
import { ContentLibraryValidationService } from '../content-library-validation.service';
import {
    ContentLibrarySectionName,
    SyncValidationStateSection,
} from '@r1-compass-library/features/validation-sync';

type RuleWithValidation = { valid: boolean } & ContentAssetRule;
@Component({
    selector: 'r1-compass-app-content-library-rules',
    imports: [
        InstructionsComponent,
        DragDropModule,
        FontAwesomeModule,
        MatButtonModule,
        MatMenuModule,
        ContentLibraryRuleSummaryPipe,
    ],
    templateUrl: './content-library-rules.component.html',
    styleUrl: './content-library-rules.component.scss',
})
export class ContentLibraryRulesComponent implements OnInit, FormUnsavedChangesWarningContentLibraryRules {
    readonly #assetStore = inject(SelectedContentAssetStore);
    readonly #dataMartTablesStore = inject(DataMartTablesStore);
    readonly #router = inject(Router);
    readonly #route = inject(ActivatedRoute);
    readonly #globalLoggedInUserStore = inject(GlobalLoggedInUserStore);
    readonly #contentAssetStore = inject(SelectedContentAssetStore);
    readonly #assetStateMessageHandlingService = inject(AssetStateMessageHandlingService);
    readonly #formSaveWarningStore = inject(SaveGuardStore);
    readonly #validationStateService = inject(ContentLibraryValidationStateService);
    readonly #validationService = inject(ContentLibraryValidationService);

    fields: Signal<DataMartField[] | undefined> = this.#dataMartTablesStore.fields;
    asset: Signal<ContentAsset | undefined> = toSignal(this.#assetStore.asset$);

    rulesWithValidation = computed<RuleWithValidation[]>(() => {
        const asset = this.asset();
        if (!asset) return [];

        return (
            asset.rules?.map(rule => ({
                ...rule,
                valid: this.validateRule(rule, asset.playImporterId),
            })) ?? []
        );
    });

    loggedInUserId: Signal<number | undefined> = this.#globalLoggedInUserStore.selectedUserId;
    faEllipsisVertical = faEllipsisVertical;

    canDeactivate$ = new BehaviorSubject({ dirtied: false, valid: true });
    saved$ = new Subject<void>();

    private selectedIamDivision$ = toObservable(this.#globalLoggedInUserStore.selectedIamDivision);

    saveGuardHandlerFn = this.#formSaveWarningStore.formUnsavedChangesWarningContentLibraryRules;

    private readonly canDeactivate = toSignal(this.canDeactivate$, { requireSync: true });

    public disableSave = computed<boolean>(() =>
        this.#validationStateService.shouldDisableSave(
            this.asset(),
            this.canDeactivate(),
            this.#validationStateService.invalidSections(),
            'rules'
        )
    );

    constructor() {
        const library = inject(FaIconLibrary);

        library.addIcons(faEllipsisVertical);

        this.saveGuardHandlerFn({
            saved$: this.saved$,
            canDeactivate$: this.canDeactivate$,
            disableSave: this.disableSave,
        });
    }
    ngOnInit(): void {
        this.selectedIamDivision$.pipe(filterNil()).subscribe(selectedIamDivision => {
            const tableName = this.#globalLoggedInUserStore.selectedDivisionConfig()?.config?.mapping?.dataMartTable;
            if (tableName) {
                this.#dataMartTablesStore.getTable({ tableName: tableName });
            }
        });
        this.#contentAssetStore.message$.pipe(filterNil()).subscribe(message => {
            this.#assetStateMessageHandlingService.handleMessage(message);
            this.#contentAssetStore.resetMessage();
        });

        const assetId = this.#route.snapshot.paramMap.get('assetId');
        if (assetId && assetId !== 'new' && this.#assetStore.asset()?.id !== +assetId) {
            this.#assetStore.getAsset({ id: +assetId });
        }
    }

    onAddRule() {
        this.#router.navigate([`new`], { relativeTo: this.#route });
    }

    drop(event: CdkDragDrop<ContentAssetRule[]>) {
        if (event.previousIndex !== event.currentIndex) {
            this.canDeactivate$.next({ dirtied: true, valid: true });
        }

        moveItemInArray(this.asset()?.rules ?? [], event.previousIndex, event.currentIndex);
    }

    onEdit(rule: ContentAssetRule) {
        this.#router.navigate([`${rule.id}/edit-rule`], { relativeTo: this.#route });
    }

    onCopy(asset: ContentAsset, rule: ContentAssetRule) {
        const copy = {
            ...rule,
            id: Uuid.generate(),
            name: rule.name + ' Copy',
        };
        this.#assetStore.saveAsset({ asset: { ...asset, rules: [...(asset.rules ?? []), copy] } });
    }

    onDelete(asset: ContentAsset, deleted: ContentAssetRule) {
        this.#assetStore.saveAsset({ asset: { ...asset, rules: asset.rules?.filter(rule => rule.id !== deleted.id) } });
    }

    onSave() {
        const asset = {
            ...this.asset(),
            updatedBy: this.loggedInUserId(),
            valid: this.#validationStateService.invalidSections().length === 0,
        };

        if (asset) {
            return this.#assetStore.saveAsset({ asset: asset });
        } else {
            return noopFormSaveMutationResult();
        }
    }

    validateRule(rule: ContentAssetRule, playImporterId: number | undefined) {
        return this.#validationService.buildRuleForm(rule, playImporterId).valid;
    }
}
